import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Seo } from "../components/Seo";
import { ProductCard } from "../components/ProductCard";
import { getProducts } from "../lib/api";

const FILTERS = [
  { key: "All Products", match: null },
  { key: "Men's Health", match: "Men's Health" },
  { key: "Focus & Sleep", match: "Focus & Sleep Health" },
  { key: "Pain Relief", match: "Pain Management" },
  { key: "Antibiotics", match: "Antibiotics" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "OneCare Pharmacy product catalogue",
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All Products");
  const [query, setQuery] = useState("");

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    return products.filter((p) => {
      const inFilter = !f?.match || p.category === f.match;
      const q = query.trim().toLowerCase();
      const inQuery = !q || `${p.name} ${p.strength}`.toLowerCase().includes(q);
      return inFilter && inQuery;
    });
  }, [products, filter, query]);

  return (
    <main className="bg-[#F8FAFC]">
      <Seo
        title="OneCare Pharmacy Products | Product Information & Prices"
        description="Browse OneCare Pharmacy product information, pack sizes and displayed prices. Contact our team to confirm availability and product details."
        path="/products"
        jsonLd={jsonLd}
      />

      <section className="bg-white pb-12 pt-16 sm:pt-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-700">PRODUCTS</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Product catalogue and displayed prices
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Browse pack sizes and displayed prices, add items to your enquiry basket, and checkout flows you straight
            into the enquiry form — our team confirms availability and details by email.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter products by category">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`filter-pill-${f.key.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setFilter(f.key)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,border-color,color] ${
                    filter === f.key
                      ? "border-[#0D5C46] bg-[#0D5C46] text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-emerald-600/40 hover:text-emerald-800"
                  }`}
                >
                  {f.key}
                </button>
              ))}
            </div>
            <div className="relative md:w-72">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="product-search-input"
                placeholder="Search product or strength..."
                aria-label="Search products"
                className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-800 outline-none transition-[border-color] focus:border-emerald-600"
              />
            </div>
          </div>

          {visible.length > 0 ? (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {visible.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          ) : (
            <p data-testid="products-empty-state" className="mt-14 text-center text-sm text-slate-500">
              No products match your search. Try a different name or strength, or send us an enquiry.
            </p>
          )}

          <p className="mt-14 border-t border-slate-200 pt-8 text-center text-xs leading-relaxed text-slate-400">
            Product availability, pack details and supply eligibility are confirmed per market before any supply.
            Use only verified manufacturer or authorised-distributor pack photographs in the final listing.
          </p>
        </div>
      </section>
    </main>
  );
}
